import { useEffect, useState } from "react";
import { MemberPaidDTO } from "../../model/entity/MemberPaidDTO";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { Edit, ThumbDown, ThumbUp } from "@mui/icons-material";
import { OverviewFilterField } from "../../components/OverviewFilterField";
import { useNavigate } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr } from "../../components/Functions";
import { getFetcher } from "../../components/Fetchers";

const MemberOverview = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [members, setMembers] = useState<MemberPaidDTO[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filter, setFilter] = useState("");
  const [onlyActive, setOnlyActive] = useState("true");
  const [paid, setPaid] = useState("all");

  useEffect(() => {
    const fetchMembers = async () => {
      const config = {
        search: filter,
        onlyActive: onlyActive,
        paid: paid,
        page: page,
        size: rowsPerPage,
      };
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/members/all`,
          config
        );
        setMembers(response.data.content);
        setTotalElements(response.data.totalElements);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchMembers();
  }, [page, rowsPerPage, filter, onlyActive, paid]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeOnlyActive = () => {
    setPage(0);
    setOnlyActive(onlyActive === "true" ? "false" : "true");
  };

  const handleChangePaid = (
    event: unknown,
    value: string,
    checked: boolean
  ) => {
    setPage(0);
    setPaid(checked ? value : "all");
  };

  return (
    <div className="tableContainer">
      <div className="aboveTableWrapper">
        <div>
          <Button
            className="submitBtn"
            onClick={() => {
              navigate("/overview/members/add-new");
            }}
          >
            Nový člen
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={onlyActive === "true"}
                onChange={handleChangeOnlyActive}
              />
            }
            label="Pouze aktivní"
            sx={{ ml: "1rem" }}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={paid === "true"}
            label="Zaplaceno"
            value="true"
            disabled={onlyActive === "false"}
            onChange={(e, b) => {
              handleChangePaid(e, "true", b);
            }}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={paid === "false"}
            label="Nezaplaceno"
            value="false"
            disabled={onlyActive === "false"}
            onChange={(e, b) => {
              handleChangePaid(e, "false", b);
            }}
          />
        </div>
        <div>
          <OverviewFilterField setFilter={setFilter} />
        </div>
      </div>
      <Paper
        className="tableContainer"
        elevation={members.length ? 1 : 0}
        sx={{
          width: "70%",
          maxHeight: "48rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
          <Table size="small">
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 2,
              }}
            >
              <TableRow className="tableHeader">
                <TableCell className="tableHeaderCell tableHeaderFirstCell" width={"25%"}>
                  Jméno
                </TableCell>
                <TableCell className="tableHeaderCell">Email</TableCell>
                <TableCell className="tableHeaderCell">Telefon</TableCell>
                {onlyActive === "true" && (
                  <TableCell className="tableHeaderCell">Zaplatil</TableCell>
                )}
                <TableCell className="tableHeaderCell tableHeaderLastCell">
                  Akce
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id} className="tableRow">
                  <TableCell className="tableCell tableFirstCell">
                    {member.firstName + " " + member.lastName}
                  </TableCell>
                  <TableCell className="tableCell">{member.email}</TableCell>
                  <TableCell className="tableCell">{member.phone}</TableCell>
                  {onlyActive === "true" && (
                    <TableCell className="tableCell">
                      {member.paid ? (
                        <ThumbUp className="thumbUp" />
                      ) : (
                        <ThumbDown className="thumbDown" />
                      )}
                    </TableCell>
                  )}
                  <TableCell
                    className="tableCell tableLastCell"
                    onClick={() =>
                      navigate(`/overview/members/edit/${member.id}`)
                    }
                  >
                    <Edit className="editBtn" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ backgroundColor: "white", width: "100%" }}>
          <TablePagination
            className="tablePagination"
            rowsPerPageOptions={[5, 10, 20, 999]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </div>
  );
};

export default MemberOverview;
